// @intent: Per-target, per-prop tween cancellation so a later write (a new tween OR a direct layer.set) on the
//   same property of the same render object STOPS the still-running tween that was driving that property.
// @flow: createPixiPlayableApi creates one registry per setup() run. api.tween registers the props it will write
//   (registerTween) -> any running tween touching those props on the same target is stopped first. layer.set
//   reports the props it writes synchronously (cancelTweensForProps) -> the explicit set wins over a live tween.
// @stale: The match-3 holes bug (2026-06-01): onDrag.end faded matched tiles to alpha:0 (200ms) then synchronously
//   refreshBoardLayers() set the refilled tiles alpha:1; with NO cancellation the still-running alpha:0 tween drove
//   the refilled tiles permanently invisible. This registry makes the last write (set or newer tween) authoritative.

import type { PlayableTweenHandle } from '@mintyads/playable-api';

/**
 * @intent A running tween record: the target object it mutates, the set of prop keys it writes, and a stop fn.
 * @flow registerTween records this; cancellation calls stop() (idempotent) and drops the record.
 * @stale Keep `props` aligned with the Pixi-local prop keys actually written (x/y/alpha/width/height/rotation/scale).
 */
interface TrackedTween {
  readonly props: ReadonlySet<string>;
  stop(): void;
}

/**
 * @intent Per-target+per-prop cancellation registry. Keyed on the REAL mutable render object (the same object that
 *   both layer.set and api.tween mutate), so the two paths share one truth about who currently drives each prop.
 * @flow registerTween(target, props, handle): stop any running tween on `target` that writes any of `props`, then
 *   record this one. cancelTweensForProps(target, props): stop any running tween on `target` writing any of `props`
 *   (used by layer.set so an explicit prop write overrides a live tween). releaseTween clears a finished record.
 * @stale Uses a WeakMap keyed on the target object, so records are GC'd with the render node; no manual teardown.
 */
export interface TweenConflictRegistry {
  registerTween(target: object, props: ReadonlySet<string>, handle: PlayableTweenHandle): void;
  cancelTweensForProps(target: object, props: Iterable<string>): void;
  releaseTween(target: object, handle: PlayableTweenHandle): void;
}

export function createTweenConflictRegistry(): TweenConflictRegistry {
  // target object -> (tween handle -> tracked record). A Map (not WeakMap) for the inner level so we can iterate
  // the live tweens of a target; the OUTER WeakMap drops the whole entry when the render object is collected.
  const byTarget = new WeakMap<object, Map<PlayableTweenHandle, TrackedTween>>();

  const stopConflicting = (target: object, props: Iterable<string>): void => {
    const tweens = byTarget.get(target);
    if (!tweens || tweens.size === 0) return;
    const wanted = props instanceof Set ? props : new Set(props);
    if (wanted.size === 0) return;
    for (const [handle, tracked] of [...tweens]) {
      let conflicts = false;
      for (const prop of tracked.props) {
        if (wanted.has(prop)) { conflicts = true; break; }
      }
      if (!conflicts) continue;
      tweens.delete(handle);
      tracked.stop();
    }
  };

  return {
    registerTween(target, props, handle) {
      stopConflicting(target, props);
      let tweens = byTarget.get(target);
      if (!tweens) {
        tweens = new Map<PlayableTweenHandle, TrackedTween>();
        byTarget.set(target, tweens);
      }
      tweens.set(handle, { props, stop: () => { handle.stop(); } });
    },
    cancelTweensForProps(target, props) {
      stopConflicting(target, props);
    },
    releaseTween(target, handle) {
      const tweens = byTarget.get(target);
      if (!tweens) return;
      tweens.delete(handle);
    },
  };
}
